import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy, Optional, Renderer2 } from '@angular/core';
import { NgControl, NgForm, NgModel } from '@angular/forms';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subject, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import each from 'lodash-es/each';

/**
 * @deprecated Move to template style form inputs components
 */
@Directive({
    selector: 'input[muiInput], textarea[muiInput], select[muiInput]',
    standalone: true,
    host: {
        '(blur)': 'this.toggleClass($event)',
        '(focus)': 'this.toggleClass($event)',
        '(input)': 'this.toggleClass($event)'
    }
})
export class MuiInputDirective implements AfterViewInit, OnDestroy {
    protected labelElement?: HTMLElement;
    protected ngUnsubscribe$: Subject<void> = new Subject<void>();
    private _inputObserver;
    private _listenerInput?: () => void;

    protected control!: NgControl;
    protected form?: NgForm;

    private setClassTimeout?: number;

    constructor(
        @Inject(DOCUMENT) protected document: Document,
        protected elementRef: ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        protected renderer2: Renderer2,
        @Optional() ngForm: NgForm,
        @Optional() ngModel: NgModel,
        @Optional() ngControl: NgControl,
        private timeoutService: TimeoutService
    ) {
        if (ngControl) {
            this.control = ngControl;
        }

        if (ngModel) {
            this.control = ngModel;
        }

        if (ngForm) {
            this.form = ngForm;
        }
    }

    @Input()
    set muiInput(label: string) {
        if (label) {
            if (this.labelElement) {
                this.labelElement.remove();
                this.labelElement = undefined;
            }
            const labelElement = this.renderer2.createElement('label');
            const wrapper = this.wrapper;
            this.renderer2.setProperty(labelElement, 'innerText', label);
            wrapper.insertBefore(labelElement, this.element.nextSibling);
            this.renderer2.addClass(wrapper, 'has-label');
            this.labelElement = labelElement;
        }

        if (this.element && this.element.tagName === 'SELECT') {
            this.renderer2.addClass(this.wrapper, 'is-select');
        }
    }

    @Input()
    muiInputAlwaysFocusedState = false;

    @Input()
    muiInputValidateOnSubmit = true;

    ngAfterViewInit() {
        this.renderer2.addClass(this.wrapper, 'mui-input');
        this.setClassTimeout = this.timeoutService.setTimeout(() => this.toggleClass(), 200);

        if (this.control) {
            merge(this.control.valueChanges!, this.control.statusChanges!)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.toggleClass());
        }

        this._listenerInput = this.renderer2.listen(this.element, 'change', () => {
            this.toggleClass();
        });

        if (typeof MutationObserver !== 'undefined') {
            this._inputObserver = new MutationObserver(e => {
                this.toggleClass();
            }).observe(this.element, {
                childList: true,
                attributes: true,
                subtree: true
            });
        }

        if (this.form) {
            this.form.ngSubmit.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.toggleClass());
        }
    }

    ngOnDestroy() {
        if (this.setClassTimeout) {
            clearTimeout(this.setClassTimeout);
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        if (this._inputObserver) {
            this._inputObserver.disconnect();
        }

        if (this._listenerInput) {
            this._listenerInput();
        }
    }

    toggleClass(event?: Event) {
        const wrapper = this.wrapper;
        if (!wrapper) {
            return;
        }
        /** is not empty */
        const isNotEmpty: boolean = coerceBooleanProperty(!!(this.element.value && this.element.value.length));

        /** is required */
        const isRequired: boolean = coerceBooleanProperty(this.element.required);

        /** is form submitted */
        const isSubmitted: boolean | undefined = this.form ? coerceBooleanProperty(this.form.submitAttempt) : undefined;

        /** element is invalid */
        const isInvalid: boolean = this.element.classList.contains('ng-invalid');

        /** element is valid */
        const isValid: boolean = this.element.classList.contains('ng-valid');

        /** if input is disabled */
        const isDisabled = this.element.disabled;

        const classes: { [key: string]: boolean } = {};
        classes['is-not-empty'] = isNotEmpty;

        if (!this.control) {
            const invalid =
                (this.form && this.muiInputValidateOnSubmit ? isSubmitted : isInvalid && isRequired) && isNotEmpty && !isDisabled;

            classes['is-invalid'] = !!invalid;
            classes['is-valid'] = isValid && isRequired && isNotEmpty;
        } else {
            const invalid = this.control.invalid && (this.form && this.muiInputValidateOnSubmit ? isSubmitted : isNotEmpty) && !isDisabled;

            classes['is-invalid'] = !!invalid;
            classes['is-valid'] = !!(this.control.valid && isRequired);
            classes['is-pending'] = !!this.control.pending;
        }

        each(classes, (value, key) => {
            if (value) {
                this.renderer2.addClass(wrapper, key);
            } else {
                this.renderer2.removeClass(wrapper, key);
            }
        });

        if (this.muiInputAlwaysFocusedState) {
            this.renderer2.addClass(wrapper, 'is-focused');
        } else {
            if (event && event.type) {
                if (event.type === 'focus') {
                    this.renderer2.addClass(wrapper, 'is-focused');
                } else if (event.type === 'blur') {
                    this.renderer2.removeClass(wrapper, 'is-focused');
                }
            }
        }
    }

    get element(): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
        return this.elementRef.nativeElement;
    }

    get wrapper(): HTMLElement {
        let element = this.element as HTMLElement;
        do {
            element = element.parentElement as HTMLElement;
        } while (element?.hasAttribute('noMuiWrapper'));
        return element;
    }
}
